import React, { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { themeGet } from '@styled-system/theme-get';
import styled from 'styled-components';
import { transparentize } from 'polished';
import { color } from 'styled-system';
import Search from '../../../shared/Search';
import Text from '../../../shared/Text';

const SearchWrapper = styled.div`
  margin: 16px 20px;
`;

const BoardsList = styled.ul`
  max-height: 84vh;
  display: flex;
  flex-direction: column;
  overflow: auto;

  scrollbar-width: thin;
  scrollbar-color: rgba(196, 196, 196, 0.24);

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background-color: rgba(196, 196, 196, 0.24);
  }

  &:after {
    display: ${({ hasFade }) => (hasFade ? 'block' : 'none')};
    content: '';
    position: absolute;
    z-index: 1;
    bottom: 0;
    left: 0;
    pointer-events: none;
    background-image: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0),
      rgba(255, 255, 255, 1) 95%
    );
    width: 100%;
    height: 4em;
    border-radius: 8px;
  }

  ${themeGet('mediaQueries.forMediumUp')} {
    max-height: 360px;
  }
`;

const BoardLink = styled.a`
  ${color}
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BoardListItem = styled.li`
  padding: 11px 20px;

  &:hover {
    background-color: ${props =>
      transparentize(0.96, themeGet('colors.primary.100')(props))};
  }
`;

const ArchivedLabel = styled.span`
  border: 1px solid ${themeGet('colors.black.40')};
  border-radius: 64px;
  padding: 2px 9px;
  background: ${themeGet('colors.black.5')};
  margin-right: 7px;
`;

const checkIfHasFade = (el, setFadeState) => {
  const scrollExist =
    Boolean(el.current) && el.current.scrollHeight > el.current.clientHeight;

  setFadeState(scrollExist);
};

const Boards = ({ boards, excludeCloseIcon }) => {
  const [searchValue, setSearchValue] = useState('');
  const [searchBoards, setSearchBoards] = useState(boards);
  const boardsListRef = useRef(null);
  const [hasFade, setHasFade] = useState(false);
  const { t } = useTranslation('header');
  const [boardQuery, setBoardQuery] = useState('');

  useLayoutEffect(() => {
    checkIfHasFade(boardsListRef, setHasFade);
  }, [searchBoards]);

  const handleScroll = () => {
    if (boardsListRef.current) {
      const { scrollHeight, scrollTop, clientHeight } = boardsListRef.current;

      setHasFade(scrollHeight - scrollTop !== clientHeight);
    }
  };

  const search = {
    boardQuery,
    executeSearch: value => {
      setSearchValue(value);
      if (value === '') {
        setSearchBoards(boards);
      } else {
        setSearchBoards(
          boards.filter(
            ({ name }) => name.toLowerCase().indexOf(value.toLowerCase()) > -1,
          ),
        );
      }
    },
    clearSearch: () => {
      setBoardQuery('');
      setSearchBoards(boards);
    },
  };

  return (
    <>
      <SearchWrapper>
        <Search
          executeSearch={search.executeSearch}
          searchInitialValue={search.nameQuery}
          clearSearch={search.clearSearch}
          excludeCloseIcon={excludeCloseIcon}
          placeholder="Search"
          fontSize="16px"
          imgWidth="18px"
          imgHeight="18px"
        />
      </SearchWrapper>
      {searchBoards.length === 0 ? (
        <Text variant="subtitle2" color="black.80" p="0 20px" as="p">
          {t('boardSelect.noResults', { searchValue })}
        </Text>
      ) : (
        <BoardsList
          onScroll={handleScroll}
          ref={boardsListRef}
          hasFade={hasFade}
        >
          {searchBoards.map(board => (
            <BoardListItem key={board.link}>
              <BoardLink
                color={board.isArchived ? 'black.60' : 'black.80'}
                data-method="patch"
                href={board.link}
              >
                {board.isArchived && (
                  <ArchivedLabel>
                    <Text variant="small2" color="black.60">
                      {t('boardSelect.archived')}
                    </Text>
                  </ArchivedLabel>
                )}
                {board.name}
              </BoardLink>
            </BoardListItem>
          ))}
        </BoardsList>
      )}
    </>
  );
};

export default Boards;
